import React, { useEffect, useState } from "react";
import { useFormContext, FieldErrors, UseFormSetValue } from "react-hook-form";
import FormTitle from "../../../components/FormTitle";
import { CheckboxOption } from "../../../service/interfaces/ApiResponse.interface";
import { ApiService } from "../../../service/api.service";
import { MASTER_URL } from "../../../common/common";

interface CompanyTypeData {
  clientId: string;
  entityId: string;
  businessTypeId: string;
  value: string;
}

interface CompanyTypeSelectorProps {
  errors: FieldErrors;
  setValue: UseFormSetValue<any>;
  defaultValues?: CompanyTypeData[] | string[]; 
  isEditMode?: boolean;
}

const CompanyTypeSelector: React.FC<CompanyTypeSelectorProps> = ({ 
  errors, 
  setValue, 
  defaultValues = [],
  isEditMode = false
}) => {
  const { watch, clearErrors, register } = useFormContext();
  const apiService = new ApiService();
  const [companyTypes, setCompanyTypes] = useState<CheckboxOption[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const formCompanyTypes = watch("companyTypes") || [];
  useEffect(() => {
    register("companyTypes", { 
      required: "Please select at least one company type" 
    });
  }, [register]);

  useEffect(() => {
    if (isEditMode && defaultValues.length > 0 && !isInitialized) {
      if (typeof defaultValues[0] === 'object' && 'businessTypeId' in defaultValues[0]) {
        const businessTypeIds = (defaultValues as CompanyTypeData[]).map(item => item.businessTypeId);
        // console.log("Extracted businessTypeIds:", businessTypeIds);
        setValue("companyTypes", businessTypeIds);
      } else {
        setValue("companyTypes", defaultValues);
      }
      setIsInitialized(true);
      // console.log("Initialized with default values");
    }
  }, [isEditMode, defaultValues, setValue, isInitialized]);

  useEffect(() => {
    const getCompanyType = async () => {
      try {
        const response: any = await apiService.getApi(`${MASTER_URL}/booking/data-route?key=Booking Type`);
        // console.log("API Response for Company Types:", response);

        if (response.status === "200" && response.body["Company Type"]) {
          const types = response.body["Company Type"].map((item: any) => ({
            label: item.value,
            value: item.metaId,
          }));
          setCompanyTypes(types);
          // console.log("Mapped company types:", types);

          if (isEditMode && defaultValues.length > 0 && !formCompanyTypes.length) {
            if (typeof defaultValues[0] === 'object' && 'businessTypeId' in defaultValues[0]) {
              const businessTypeIds = (defaultValues as CompanyTypeData[]).map(item => item.businessTypeId);
              setValue("companyTypes", businessTypeIds);
              // console.log("Setting businessTypeIds after fetch:", businessTypeIds);
            } else {
              setValue("companyTypes", defaultValues);
              console.log("Setting default values after fetch:", defaultValues);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching company types:", error);
      }
    };

    getCompanyType();
  }, []); 

  const handleCheckboxChange = (value: string) => {
    // console.log("Checkbox changed:", value);
    // console.log("Current form values:", formCompanyTypes);
    
    let updatedSelectedTypes;
    
    if (formCompanyTypes.includes(value)) {
      updatedSelectedTypes = formCompanyTypes.filter((type: string) => type !== value);
    } else {
      updatedSelectedTypes = [...formCompanyTypes, value];
    }
    
    // console.log("Updated selected types:", updatedSelectedTypes);
    
    setValue("companyTypes", updatedSelectedTypes, { shouldValidate: true });
    if (updatedSelectedTypes.length > 0) {
      clearErrors("companyTypes");
    }
  };

  // console.log("Rendering with formCompanyTypes:", formCompanyTypes);

  return (
<div className="flex flex-col  p-4 ">
      <div className="flex flex-col space-y-4">
        <FormTitle title="Company Type" />
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {companyTypes.map((type) => (
            <label 
              key={type.value} 
              className="flex items-center space-x-3 font-medium text-sm py-1"
            >
              <input
                type="checkbox"
                value={type.value}
                checked={formCompanyTypes.includes(type.value)}
                onChange={() => handleCheckboxChange(type.value)}
                className="w-5 h-5 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="whitespace-nowrap">{type.label}</span>
            </label>
          ))}
        </div>
        
        {errors.companyTypes && (
          <p className="text-red-500 text-xs mt-1">
            {String(errors.companyTypes.message)}
          </p>
        )}
      </div>
    </div>
  );
};

export default CompanyTypeSelector;